/**
 * The media slice — five kinds, one implementation.
 *
 * Not a face: `entries/` is one domain with one production face (ADR 0014:114-116).
 *
 * The largest slice in step 2, and the only one binding five entry kinds to one set of surfaces.
 * `EntryOf` keys each kind to its own entry type rather than to the `MediaEntry` union — the union
 * form compiles and silently discards every per-kind type (#359) — so "one slice" is one
 * implementation over five bindings, not one type over five keys.
 */

export type {
  ArtistEntry,
  ArtistMeta,
  BookEntry,
  BookMeta,
  BookStatus,
  LabelEntry,
  LabelMeta,
  MediaEntry,
  MediaKind,
  MediaMeta,
  MediaMetadataMap,
  MovieEntry,
  MovieMeta,
  MovieStatus,
  ReleaseEntry,
  ReleaseMeta,
  SecondBrainBookStatus,
} from './meta'
export { BOOK_STATUSES, MEDIA_KINDS, MOVIE_STATUSES } from './meta'
export {
  isArtistEntry,
  isBookEntry,
  isLabelEntry,
  isMediaKind,
  isMovieEntry,
  isReleaseEntry,
} from './guard'

export { mediaEntryDraft } from './write-spec'
export type { MediaCreateInput } from './write-spec'
export type {
  ArtistCreate,
  BookCreate,
  LabelCreate,
  MovieCreate,
  ReleaseCreate,
} from './write-spec'
export {
  useCreateArtist,
  useCreateBook,
  useCreateLabel,
  useCreateMovie,
  useCreateRelease,
  useDeleteArtist,
  useDeleteBook,
  useDeleteLabel,
  useDeleteMovie,
  useDeleteRelease,
  useUpdateArtist,
  useUpdateBook,
  useUpdateLabel,
  useUpdateMovie,
  useUpdateRelease,
} from './write-spec'

export type { EntryWithRelations, LibraryData } from './select-library'
export {
  libraryQueryKey,
  useInvalidateLibrary,
  useLibraryData,
  usePrefetchLibrary,
} from './select-library'

export { ArtistRow, LabelRow, ReleaseRow } from './music-rows'
export {
  ArtistEditForm,
  LabelEditForm,
  ReleaseEditForm,
} from './music/EditForms'
export { BookRow } from './book-row'
export { MovieRow } from './movie-row'
export { ArtistCard, BookCard, LabelCard, MovieCard } from './cards'
export { BooksView } from './books-view'
export { MoviesView } from './movies-view'
export { MusicView } from './music-view'
